import type IntlService from 'ember-intl/services/intl';
import type { TasFieldOption as TasFieldOptionModel } from 'tio-data/data/resources/tas-field-option.type';
import type { CustomFieldOption, CustomFieldSignature } from '../../types/tuition-assistance.ts';
import { formatISODateToDisplay } from '../date/index.ts';
import { formatAsDollars } from '../format.ts';

type StoredValue = string | boolean | number | null | undefined;

type FieldOptionRecord = Pick<TasFieldOptionModel, 'fieldName' | 'value' | 'label'>;

type Choice = { label: string | null | undefined; value: unknown };

export interface CustomFieldDisplayContext {
  /** The program template's `tas-field-option` records; a select offers the ones keyed by its id. */
  fieldOptions?: readonly FieldOptionRecord[];
  /** Without it, dates, booleans and unmatched choices render exactly as stored. */
  intl?: IntlService;
}

const isBlank = (value: StoredValue) =>
  value === null || value === undefined || (typeof value === 'string' && value.trim() === '');

const isSameValue = (candidate: unknown, value: StoredValue) =>
  candidate === value ||
  (candidate !== null &&
    candidate !== undefined &&
    value !== null &&
    value !== undefined &&
    String(candidate).toLowerCase() === String(value).toLowerCase());

const isBooleanValue = (value: StoredValue) =>
  typeof value === 'boolean' || ['true', 'false'].includes(String(value).toLowerCase());

/**
 * Inline options come from the copy holding the answer, never the template: the classic builder
 * renumbers and re-slugs option values, so the template can map the same value to another choice.
 */
const choicesFor = (
  field: CustomFieldSignature,
  fieldOptions: readonly FieldOptionRecord[]
): Choice[] => {
  const inline = ((field.options ?? []) as readonly (CustomFieldOption | string)[]).map((option) =>
    typeof option === 'string' ? { label: option, value: option } : option
  );
  if (field.input !== 'select') return inline;

  const identifier = field.id || field.name;
  const records = identifier
    ? fieldOptions.filter((option) => option.fieldName === identifier)
    : [];
  return field.value_as_field_option ? [...records, ...inline] : [...inline, ...records];
};

/** Answers saved as an option's label rather than its value still resolve, value matches first. */
const findOptionLabel = (choices: Choice[], value: StoredValue) => {
  const match =
    choices.find((choice) => isSameValue(choice.value, value)) ??
    choices.find((choice) => isSameValue(choice.label, value));
  if (!match) return undefined;
  return match.label?.trim() ? match.label : String(match.value);
};

/**
 * Formats one stored custom-field answer for read-only display, first match wins: the chosen
 * option's label (its value when the label is blank), Yes/No for a boolean, a "no matching
 * option" note on a radio/select value none of its options hold, dollars for currency cents,
 * a localized date. Blank values come back unchanged.
 *
 * @param field the copy holding the answer; its own options are the ones the employee chose from
 */
export const formatReadonlyCustomFieldValue = (
  value: StoredValue,
  field: CustomFieldSignature,
  { fieldOptions = [], intl }: CustomFieldDisplayContext = {}
): StoredValue => {
  const choices = choicesFor(field, fieldOptions);
  const label = findOptionLabel(choices, value);
  if (label !== undefined) return label;
  if (isBlank(value)) return value;

  if (intl && isBooleanValue(value)) {
    return intl.t(String(value).toLowerCase() === 'true' ? 'common.yes' : 'common.no');
  }

  const isChoice = field.input === 'radio' || field.input === 'select';
  if (intl && isChoice && choices.length > 0) {
    return intl.t('tuition_assistance.unmatched_option', { value: String(value) });
  }

  if (field.inputType === 'currency') {
    const cents = Number(value);
    if (!Number.isNaN(cents)) return formatAsDollars(cents);
  }

  if (intl && field.inputType === 'date') return formatISODateToDisplay(String(value), intl);

  return value;
};

/**
 * Formats every non-blank answer a custom field holds, comma-joined, for one read-only row.
 *
 * @returns an empty string when the field is unanswered, so each surface picks its own fallback
 */
export const formatCustomFieldAnswer = (
  field: CustomFieldSignature,
  context: CustomFieldDisplayContext = {}
): string =>
  ((field.values ?? []) as readonly StoredValue[])
    .filter((value) => !isBlank(value))
    .map((value) => String(formatReadonlyCustomFieldValue(value, field, context)))
    .join(', ');
